'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Calculator, CreditCard, LogIn, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import type { Language } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';


export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const { language, setLanguage, t } = useLanguage();

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[0];

  const navLinks = [
    { href: '/', label: t.nav.rateCalculator, icon: Calculator },
    { href: '/rate-card', label: t.nav.rateCard, icon: CreditCard },
  ];

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center px-4 md:px-8 border-b border-border"
        style={{ background: 'rgba(15, 10, 30, 0.85)', backdropFilter: 'blur(16px)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={32} />
          <span className="font-extrabold text-lg tracking-tight text-gradient-primary hidden sm:block">
            VoltVision<span className="text-accent">18</span>
          </span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 ml-10">
          {navLinks?.map((link) => {
            const Icon = link?.icon;
            const isActive = pathname === link?.href;
            return (
              <Link
                key={`nav-${link?.href}`}
                href={link?.href}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                  isActive
                    ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                }`}
              >
                <Icon size={15} />
                {link?.label}
              </Link>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150"
              aria-label={t.languageSwitcher.label}
              aria-expanded={langOpen}
            >
              <span className="text-base leading-none">{currentLang.flag}</span>
              <span className="hidden sm:inline text-xs font-bold">{currentLang.nativeLabel}</span>
              <ChevronDown size={12} className={`transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>

            {langOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-44 rounded-xl border border-border shadow-2xl overflow-hidden z-50"
                style={{ background: 'rgba(15, 10, 30, 0.98)', backdropFilter: 'blur(16px)' }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={`lang-${lang.code}`}
                    onClick={() => {
                      setLanguage(lang.code as Language);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-100 ${
                      language === lang.code
                        ? 'bg-primary/20 text-primary font-semibold' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="font-medium">{lang.nativeLabel}</span>
                    {language === lang.code && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            href="/sign-up-login"
            className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              pathname === '/sign-up-login' ?'btn-primary px-4 py-2 text-sm' :'btn-ghost px-4 py-2 text-sm'
            }`}
          >
            <LogIn size={15} />
            {t.nav.signIn}
          </Link>
          <button
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="absolute top-16 left-0 right-0 border-b border-border p-4 flex flex-col gap-2"
            style={{ background: 'rgba(15, 10, 30, 0.98)', backdropFilter: 'blur(16px)' }}
            onClick={(e) => e?.stopPropagation()}
          >
            {navLinks?.map((link) => {
              const Icon = link?.icon;
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={`mobile-nav-${link?.href}`}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  <Icon size={16} />
                  {link?.label}
                </Link>
              );
            })}
            <Link
              href="/sign-up-login"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold btn-ghost mt-1"
            >
              <LogIn size={16} />
              {t.nav.signInCreate}
            </Link>
            {/* Mobile Language Switcher */}
            <div className="mt-2 pt-2 border-t border-border/40">
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider px-4 mb-2">
                {t.languageSwitcher.label}
              </p>
              <div className="grid grid-cols-2 gap-1">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={`mobile-lang-${lang.code}`}
                    onClick={() => {
                      setLanguage(lang.code as Language);
                      setMobileOpen(false);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                      language === lang.code
                        ? 'bg-primary/20 text-primary font-semibold' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="text-xs font-medium">{lang.nativeLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}